import React, { useState } from 'react';
import { 
  X, 
  Check, 
  Zap, 
  ShieldCheck, 
  Send, 
  Sparkles, 
  Lock, 
  MessageSquare, 
  CheckCircle2, 
  ExternalLink,
  Laptop,
  HelpCircle,
  Mail,
  Smartphone
} from 'lucide-react';

interface PurchaseFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPlan?: 'ANNUAL' | 'LIFETIME';
}

export const PurchaseFlowModal: React.FC<PurchaseFlowModalProps> = ({
  isOpen,
  onClose,
  initialPlan = 'LIFETIME'
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'ANNUAL' | 'LIFETIME'>(initialPlan);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [devices, setDevices] = useState(selectedPlan === 'LIFETIME' ? 'Unlimited' : '5');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [orderResult, setOrderResult] = useState<{ orderId: string; whatsappUrl: string } | null>(null);

  if (!isOpen) return null;

  const currentPrice = selectedPlan === 'LIFETIME' ? '$29.99' : '$9.99';
  const planLabel = selectedPlan === 'LIFETIME' ? 'Lifetime Remote Freedom (One-Time)' : '1-Year Remote Pass (Annual)';

  const handlePlanChange = (plan: 'ANNUAL' | 'LIFETIME') => {
    setSelectedPlan(plan);
    setDevices(plan === 'LIFETIME' ? 'Unlimited' : '5');
  };

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    setIsSubmitting(true);
    const orderId = `CK-${selectedPlan}-${Date.now().toString(36).toUpperCase()}`;

    const orderData = {
      _subject: `⚡ New CK Flow Order: ${planLabel} - ${name} (${orderId})`,
      orderId,
      name,
      email,
      phone: phone || 'Not specified',
      plan: planLabel,
      price: currentPrice,
      devices,
      notes: notes || 'N/A',
      submittedAt: new Date().toISOString()
    };

    try {
      // 1. Send to FormSubmit -> Delivered directly to chintankheradia123@gmail.com
      await fetch('https://formsubmit.co/ajax/chintankheradia123@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(orderData)
      });
    } catch (err) {
      console.warn('FormSubmit dispatch notification:', err);
    }

    try {
      // 2. Also send to Cloudflare Worker API
      await fetch('https://ck-share-relay.chintankas1.workers.dev/api/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          plan: selectedPlan,
          price: currentPrice,
          devices,
          notes
        })
      });
    } catch (err) {
      console.warn('Worker dispatch notification:', err);
    }

    const waMsg = encodeURIComponent(
      `Hi Chintan! I'd like to purchase CK Flow PRO.\n\n📦 Order Ref: ${orderId}\n💎 Plan: ${planLabel} (${currentPrice})\n👤 Name: ${name}\n📧 Email: ${email}\n📱 WhatsApp: ${phone || 'N/A'}\n💻 Devices: ${devices}\n\nPlease share payment details and generate my official license key!`
    );
    const whatsappUrl = `https://wa.me/917600477225?text=${waMsg}`;

    setOrderResult({ orderId, whatsappUrl });
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleOpenWhatsAppDirect = () => {
    const waMsg = encodeURIComponent(
      `Hi Chintan! I'd like to buy CK Flow PRO (${selectedPlan === 'LIFETIME' ? 'Lifetime $29.99' : '1-Year $9.99'}).\nName: ${name || 'Customer'}\nEmail: ${email || 'customer@example.com'}\nPlease share payment info and my license key!`
    );
    window.open(`https://wa.me/917600477225?text=${waMsg}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/85 backdrop-blur-2xl animate-fadeIn">
      
      {/* Modal Container */}
      <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#070912]/95 border border-cyan-500/30 rounded-3xl shadow-[0_25px_80px_rgba(0,0,0,0.9),0_0_50px_rgba(0,240,255,0.15)] overflow-hidden">
        
        {/* Glow Spheres */}
        <div className="absolute -top-32 -left-32 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-purple-600/15 rounded-full blur-3xl pointer-events-none" />

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-4 sm:py-5 border-b border-white/10 bg-white/[0.02] relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-glow-cyan">
              <Sparkles size={20} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-extrabold text-white font-display tracking-tight flex items-center gap-2">
                <span>CK Flow PRO</span>
                <span className="px-2 py-0.5 text-[9px] font-bold font-mono text-cyan-300 bg-cyan-500/20 border border-cyan-500/40 rounded-full">
                  Official License
                </span>
              </h2>
              <p className="text-xs text-slate-400">Glide across PCs anywhere in the world over Remote Internet</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-2xl hover:bg-white/10 transition-colors cursor-pointer"
            title="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6">
          
          {!isSubmitted ? (
            <form onSubmit={handleSubmitOrder} className="space-y-6">
              
              {/* Plan Switcher Cards */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                  Select Your Plan
                </label>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  
                  {/* 1. Annual Plan */}
                  <div
                    onClick={() => handlePlanChange('ANNUAL')}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer relative flex flex-col justify-between ${
                      selectedPlan === 'ANNUAL'
                        ? 'bg-cyan-950/30 border-cyan-400 shadow-[0_0_20px_rgba(0,240,255,0.2)]'
                        : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-200">1-Year Remote Pass</span>
                        {selectedPlan === 'ANNUAL' && <CheckCircle2 size={16} className="text-cyan-400" />}
                      </div>
                      <div className="text-2xl font-extrabold text-white font-display">
                        $9.99 <span className="text-xs font-normal text-slate-400">/ year</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-2">
                        Includes up to 5 PCs, Cloudflare encrypted relay fallback &amp; 1-year updates.
                      </p>
                    </div>
                  </div>

                  {/* 2. Lifetime Plan (Highlighted) */}
                  <div
                    onClick={() => handlePlanChange('LIFETIME')}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer relative flex flex-col justify-between ${
                      selectedPlan === 'LIFETIME'
                        ? 'bg-gradient-to-br from-purple-950/40 via-indigo-950/30 to-black border-purple-400 shadow-[0_0_25px_rgba(168,85,247,0.3)]'
                        : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-[9px] font-bold text-white shadow-md">
                      BEST VALUE • LIFETIME
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-white">Lifetime Remote Freedom</span>
                        {selectedPlan === 'LIFETIME' && <CheckCircle2 size={16} className="text-purple-400" />}
                      </div>
                      <div className="text-2xl font-extrabold text-white font-display">
                        $29.99 <span className="text-xs font-normal text-purple-300">one-time</span>
                      </div>
                      <p className="text-[11px] text-slate-300 mt-2">
                        Unlimited devices forever, zero recurring fees &amp; direct 24/7 VIP support.
                      </p>
                    </div>
                  </div>

                </div>
              </div>

              {/* Free Local Note */}
              <div className="p-3 rounded-xl bg-cyan-950/20 border border-cyan-500/20 flex items-center justify-between text-xs text-slate-300">
                <span className="flex items-center gap-1.5">
                  <Check size={14} className="text-cyan-400 flex-shrink-0" />
                  <span><strong>Local LAN Mesh is 100% Free Forever.</strong> You only need PRO for Remote Internet.</span>
                </span>
              </div>

              {/* Customer Info Fields */}
              <div className="space-y-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Your Name / Company <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Rahul Sharma"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/60 border border-white/10 focus:border-cyan-400 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Email Address <span className="text-cyan-400">*</span> (For Key Delivery)
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="e.g. name@gmail.com"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/60 border border-white/10 focus:border-cyan-400 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      WhatsApp / Phone (For Instant Key Delivery)
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="e.g. +91 9876543210"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/60 border border-white/10 focus:border-cyan-400 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Number of PCs to Link
                    </label>
                    <input
                      type="text"
                      value={devices}
                      onChange={(e) => setDevices(e.target.value)}
                      placeholder="e.g. 3 or Unlimited"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/60 border border-white/10 focus:border-cyan-400 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Notes / Custom Request (Optional)
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any specific questions, company invoice request, or payment preferences"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-black/60 border border-white/10 focus:border-cyan-400 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleOpenWhatsAppDirect}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all cursor-pointer"
                >
                  <MessageSquare size={16} />
                  <span>💬 Chat &amp; Buy on WhatsApp (+91 7600477225)</span>
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-500 hover:from-cyan-300 hover:to-purple-400 text-black font-extrabold text-xs shadow-[0_0_25px_rgba(0,240,255,0.4)] transition-all disabled:opacity-50 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      <span>Sending Order to Gmail...</span>
                    </>
                  ) : (
                    <>
                      <Send size={15} />
                      <span>⚡ Submit Order ({currentPrice})</span>
                    </>
                  )}
                </button>
              </div>

            </form>
          ) : (
            /* Order Submitted Success View */
            <div className="py-6 text-center space-y-5 animate-fadeIn">
              <div className="w-16 h-16 rounded-3xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                <CheckCircle2 size={32} />
              </div>

              <div className="space-y-2">
                <h3 className="text-xl sm:text-2xl font-extrabold text-white font-display">
                  Order Successfully Placed!
                </h3>
                <p className="text-xs text-slate-300 max-w-md mx-auto leading-relaxed">
                  Your order details for <strong>{planLabel}</strong> have been forwarded directly to <strong className="text-cyan-300">chintankheradia123@gmail.com</strong>.
                </p>
              </div>

              {orderResult && (
                <div className="p-4 rounded-2xl bg-black/60 border border-white/10 max-w-md mx-auto text-left space-y-2 text-xs">
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Order Reference:</span>
                    <strong className="font-mono text-cyan-300">{orderResult.orderId}</strong>
                  </div>
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Customer Name:</span>
                    <strong className="text-white">{name}</strong>
                  </div>
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Email:</span>
                    <strong className="text-white">{email}</strong>
                  </div>
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Plan &amp; Amount:</span>
                    <strong className="text-emerald-400">{currentPrice}</strong>
                  </div>
                </div>
              )}

              <div className="space-y-3 pt-2">
                <p className="text-xs text-slate-400">
                  To get your license key activated immediately, tap below to connect with Chintan on WhatsApp:
                </p>

                {orderResult && (
                  <a
                    href={orderResult.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all cursor-pointer"
                  >
                    <MessageSquare size={16} />
                    <span>Open WhatsApp (+91 7600477225) to Get License Key ➔</span>
                  </a>
                )}
              </div>

              <div className="pt-4 border-t border-white/10">
                <button
                  onClick={onClose}
                  className="px-5 py-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors"
                >
                  Close Window
                </button>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};
